/**
 * Objects, straight from the main nav.
 *
 * The workspace is for building the model; this is for reading what is in it.
 * A table of the nodes in a graph — click one and you get its rows.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EmptyState, ErrorNotice, Loading } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { CategoryDot } from '../components'
import { DeleteDialog } from '../components/DeleteDialog'
import { RowMenu, TableSearch } from '../components/RowMenu'
import { ObjectRowsView } from './ObjectRowsView'
import { nodeTypesApi } from '../api/brain.api'
import { invalidateModel, useGraphModel, useGraphs, useObjectTables, useRebuildObjects } from '../hooks'
import type { ObjectTable } from '../types/brain.types'
import '../styles/brain.css'
import { Constellation } from '@/shared/components/Constellation'

export function ObjectsPage() {
  const { graphId: routeGraphId } = useParams<{ graphId?: string }>()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  const graphs = useGraphs()
  const graphId = routeGraphId ?? graphs.data?.[0]?.graph_id

  if (graphs.isLoading) return <Loading label="Loading graphs…" />
  if (graphs.error) {
    return (
      <div className="page">
        <ErrorNotice error={graphs.error} onRetry={graphs.refetch} />
      </div>
    )
  }

  if (!graphs.data?.length || !graphId) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Objects</h1>
          <p>Each node type, as the table it is, and the rows inside it.</p>
        </div>
        <EmptyState
          title="No graphs yet"
          action={
            <button className="btn btn-primary" onClick={() => navigate('/brain')}>
              Create a graph
            </button>
          }
        >
          Tables appear once a graph has node types — one table per node type, created with it.
        </EmptyState>
      </div>
    )
  }

  return (
    <ObjectsBody
      graphId={graphId}
      graphs={graphs.data}
      selectedId={params.get('object') ?? undefined}
      onSelect={(nodeTypeId) => {
        const next = new URLSearchParams(params)
        if (nodeTypeId) next.set('object', nodeTypeId)
        else next.delete('object')
        setParams(next, { replace: true })
      }}
      onGraph={(id) => navigate(`/objects/${id}`)}
    />
  )
}

function ObjectsBody({
  graphId,
  graphs,
  selectedId,
  onSelect,
  onGraph,
}: {
  graphId: string
  graphs: { graph_id: string; name: string }[]
  selectedId?: string
  onSelect: (nodeTypeId: string | null) => void
  onGraph: (graphId: string) => void
}) {
  const navigate = useNavigate()
  const toast = useToast()

  const tables = useObjectTables(graphId)
  const model = useGraphModel(graphId)
  const rebuild = useRebuildObjects(graphId)

  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<ObjectTable | null>(null)

  const rows = useMemo(() => {
    const all = tables.data ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter(
      (t) =>
        t.node_type_key.toLowerCase().includes(needle) ||
        t.table_name.toLowerCase().includes(needle) ||
        t.node_type_id.toLowerCase().includes(needle) ||
        t.columns.some((c) => c.column_name.toLowerCase().includes(needle)),
    )
  }, [tables.data, search])

  const selected = tables.data?.find((t) => t.node_type_id === selectedId)
  const categoryOf = (table: ObjectTable) =>
    model.data?.node_types.find((n) => n.node_type_id === table.node_type_id)?.category ?? 'core'

  return (
    <div className="page" style={{ maxWidth: 1280 }}>
      <div className="page-header hero">
        <Constellation />
        <div className="page-header-row">
          <div>
            <h1>Objects</h1>
            <p>
              Each node type is a real table with its own columns, in its own database schema.
              Pick one to read its rows.
            </p>
          </div>
          <div className="row-tight">
            {graphs.length > 1 && (
              <select
                className="select"
                style={{ maxWidth: 230 }}
                value={graphId}
                aria-label="Graph"
                onChange={(e) => {
                  onSelect(null)
                  onGraph(e.target.value)
                }}
              >
                {graphs.map((g) => (
                  <option key={g.graph_id} value={g.graph_id}>
                    {g.name}
                  </option>
                ))}
              </select>
            )}
            <button className="btn btn-sm" onClick={() => navigate(`/brain/${graphId}?tab=nodes`)}>
              Open the graph →
            </button>
          </div>
        </div>
      </div>

      {tables.isLoading && <Loading label="Loading tables…" />}
      {tables.error && <ErrorNotice error={tables.error} onRetry={tables.refetch} />}

      {tables.data?.length === 0 && (
        <EmptyState
          title="No tables yet"
          action={
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/brain/${graphId}?tab=nodes`)}
            >
              Add a node type
            </button>
          }
        >
          A table is created for each node type you add — one per node type, with the columns
          that node type declares.
        </EmptyState>
      )}

      {selected ? (
        <>
          <button className="btn btn-ghost btn-sm" onClick={() => onSelect(null)}>
            ← All nodes
          </button>
          <div style={{ marginTop: 12 }}>
            <ObjectRowsView graphId={graphId} table={selected} />
          </div>
        </>
      ) : (
        tables.data &&
        tables.data.length > 0 && (
          <>
            <TableSearch
              value={search}
              onChange={setSearch}
              placeholder="Search nodes, tables or columns"
              count={`${rows.length} of ${tables.data.length}`}
            >
              <button
                className="btn btn-sm"
                disabled={rebuild.isPending}
                onClick={async () => {
                  try {
                    const result = await rebuild.mutateAsync()
                    toast.success(
                      `Rebuilt ${result.tables} table${result.tables === 1 ? '' : 's'}`,
                      `${result.rows} record${result.rows === 1 ? '' : 's'} re-projected.`,
                    )
                  } catch (error) {
                    toast.error(error)
                  }
                }}
              >
                {rebuild.isPending ? 'Rebuilding…' : 'Rebuild tables'}
              </button>
            </TableSearch>

            {rows.length === 0 ? (
              <EmptyState title="Nothing matches that search">
                Try a different term, or clear the box.
              </EmptyState>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Node</th>
                      <th>Table</th>
                      <th>Columns</th>
                      <th>Rows</th>
                      <th>State</th>
                      <th aria-label="Actions" style={{ width: 48 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((table) => (
                      <tr
                        key={table.object_table_id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onSelect(table.node_type_id)}
                      >
                        <td>
                          <span className="row-tight">
                            <CategoryDot category={categoryOf(table)} />
                            <strong>{table.node_type_key}</strong>
                          </span>
                          <div className="small faint mono">{table.node_type_id}</div>
                        </td>
                        <td className="mono small">{table.qualified_name}</td>
                        <td className="mono small">
                          {table.columns.length}
                          <div className="faint truncate" style={{ maxWidth: 280 }}>
                            {table.columns.map((c) => c.column_name).join(', ') || '—'}
                          </div>
                        </td>
                        <td>{table.row_count.toLocaleString()}</td>
                        <td>
                          {table.exists ? (
                            <span className="badge badge-ok">ready</span>
                          ) : (
                            <span className="badge badge-danger" title={table.last_error ?? ''}>
                              failed
                            </span>
                          )}
                        </td>
                        <td>
                          <RowMenu
                            label={`Actions for ${table.node_type_key}`}
                            items={[
                              { label: 'View data', onSelect: () => onSelect(table.node_type_id) },
                              {
                                label: 'Edit columns',
                                onSelect: () =>
                                  navigate(`/brain/${graphId}/nodes/${table.node_type_id}`),
                              },
                              {
                                label: 'Delete node type',
                                danger: true,
                                hint: 'and its records',
                                onSelect: () => setDeleting(table),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}

      {deleting && (
        <DeleteDialog
          title="Delete this node type"
          name={deleting.node_type_key}
          what="node type"
          previewPath={`/brain/graphs/${graphId}/node-types/${deleting.node_type_id}/deletion-preview`}
          onConfirm={() => nodeTypesApi.remove(graphId, deleting.node_type_id)}
          onDeleted={() => {
            if (selectedId === deleting.node_type_id) onSelect(null)
            void invalidateModel(graphId)
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  )
}
